import { FlightSegment } from 'constants/flight'
import {
  computeDepartingFeederDelta,
  computeReturningFeederDelta,
  computeUpsellModalDelta,
  resolveDepartingFeederReference,
} from 'lib/flights/feederBundleLabels'

export const FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_SINGLE = 'single'
export const FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_COMBINED = 'combined'

type BaseOptions = {
  bundleBudget?: App.Checkout.FeederBundleBudget
  occupancies: App.Occupants
  fareFamilies: Array<App.FlightFareFamily>
  isReturnType: boolean
}

type SingleModalOptions = BaseOptions & {
  mode: typeof FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_SINGLE
  departingJourney?: App.JourneyV2
}

type CombinedModalOptions = BaseOptions & {
  mode: typeof FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_COMBINED
  currentSegment: FlightSegment
  selectedDepartingFareFamily?: App.FlightFareFamily
  getOverrideTotalCost: (fareFamily: App.FlightFareFamily) => number
}

type Options = SingleModalOptions | CombinedModalOptions

function getFareFamilyTotalCost(fareFamily?: App.FlightFareFamily): number | undefined {
  if (!fareFamily) return undefined
  return fareFamily.costWithInsurance?.all ?? fareFamily.cost.all
}

function minPositive(fares: Array<number>): number {
  let min = Infinity
  for (const fare of fares) {
    if (fare > 0 && fare < min) min = fare
  }
  return Number.isFinite(min) ? min : 0
}

function getCheapestFareFamily(
  fareFamilies: Array<App.FlightFareFamily>,
  optionFares: Array<number>,
): App.FlightFareFamily | undefined {
  let cheapestIdx = -1
  let cheapest = Infinity
  optionFares.forEach((fare, idx) => {
    if (fare > 0 && fare < cheapest) {
      cheapest = fare
      cheapestIdx = idx
    }
  })
  return cheapestIdx >= 0 ? fareFamilies[cheapestIdx] : undefined
}

/**
 * Build per-option modal labels. The tile fare is the cheapest visible option
 * (also the fare rendered on the tile that opened the modal); each option's
 * delta is `max(0, optionPageDelta - tilePageDelta)`. Within-budget options
 * collapse to `+A$0` because their page delta is 0, regardless of the tile's
 * own delta.
 */
function getModalLabelsFromDeltas(
  fareFamilies: Array<App.FlightFareFamily>,
  optionFareFor: (fareFamily: App.FlightFareFamily, index: number) => number,
  pageDeltaFor: (fare: number) => number,
): Map<string, number> {
  const map = new Map<string, number>()
  const optionFares = fareFamilies.map(optionFareFor)
  const tileFare = minPositive(optionFares)
  const tileDelta = pageDeltaFor(tileFare)
  fareFamilies.forEach((fareFamily, index) => {
    const optionDelta = pageDeltaFor(optionFares[index])
    map.set(fareFamily.id, computeUpsellModalDelta({ optionDelta, tileDelta }))
  })
  return map
}

function resolveDepartingPageDelta(
  bundleBudget: App.Checkout.FeederBundleBudget,
  fareFamilies: Array<App.FlightFareFamily>,
  optionFares: Array<number>,
): (fare: number) => number {
  // Mirror the tile's resolveDepartingFeederReference call: the cheapest visible
  // fare family is the one rendered on the tile, so its journey-level overrides
  // (if any) drive the base/buffer.
  const cheapest = getCheapestFareFamily(fareFamilies, optionFares)
  const { baseBudget, bufferBudget } = resolveDepartingFeederReference({
    bundleBudget,
    optionLegBudget: cheapest?.feederEstimatedPrice,
    optionLegBuffer: cheapest?.feederBufferPrice,
  })
  return (fare: number) => computeDepartingFeederDelta({ optionFare: fare, baseBudget, bufferBudget })
}

function resolveReturningPageDelta(
  bundleBudget: App.Checkout.FeederBundleBudget,
  selectedDepartingFare: number,
  shownDepartingDelta: number,
): (fare: number) => number {
  const baseBudget = bundleBudget.tripBudget ?? 0
  const bufferBudget = bundleBudget.tripBuffer ?? 0
  return (fare: number) => computeReturningFeederDelta({
    selectedDepartingFare,
    optionFare: fare,
    baseBudget,
    bufferBudget,
    shownDepartingDelta,
  })
}

function getSingleModalFeederBudgetLabels({
  fareFamilies,
  bundleBudget,
  departingJourney,
}: SingleModalOptions & { bundleBudget: App.Checkout.FeederBundleBudget }): Map<string, number> {
  const optionFareFor = (ff: App.FlightFareFamily): number => getFareFamilyTotalCost(ff) ?? 0
  const optionFares = fareFamilies.map(optionFareFor)
  if (departingJourney) {
    // Returning page modal: trip budget reference; shownDepartingDelta derived
    // from the selected departing journey via the Departing formula.
    const selectedDepartingFare = departingJourney.price?.all?.totalFare ?? 0
    const depRef = resolveDepartingFeederReference({
      bundleBudget,
      optionLegBudget: departingJourney.feederEstimatedPrice,
      optionLegBuffer: departingJourney.feederBufferPrice,
    })
    const shownDepartingDelta = computeDepartingFeederDelta({
      optionFare: selectedDepartingFare,
      baseBudget: depRef.baseBudget,
      bufferBudget: depRef.bufferBudget,
    })
    return getModalLabelsFromDeltas(
      fareFamilies,
      optionFareFor,
      resolveReturningPageDelta(bundleBudget, selectedDepartingFare, shownDepartingDelta),
    )
  }
  return getModalLabelsFromDeltas(
    fareFamilies,
    optionFareFor,
    resolveDepartingPageDelta(bundleBudget, fareFamilies, optionFares),
  )
}

function getCombinedModalFeederBudgetLabels({
  fareFamilies,
  currentSegment,
  selectedDepartingFareFamily,
  getOverrideTotalCost,
  isReturnType,
  bundleBudget,
}: CombinedModalOptions & { bundleBudget: App.Checkout.FeederBundleBudget }): Map<string, number> {
  const isReturningSegment = currentSegment === FlightSegment.RETURNING
  // Departing tab on return-type bookings uses merged dep+ret override totals;
  // every other case uses the fare family's standalone leg fare.
  const optionFareFor = (fareFamily: App.FlightFareFamily): number => {
    if (!isReturningSegment && isReturnType) return getOverrideTotalCost(fareFamily)
    return getFareFamilyTotalCost(fareFamily) ?? 0
  }
  const optionFares = fareFamilies.map(optionFareFor)
  if (isReturningSegment) {
    const selectedDepartingFare = getFareFamilyTotalCost(selectedDepartingFareFamily) ?? 0
    const depRef = resolveDepartingFeederReference({
      bundleBudget,
      optionLegBudget: selectedDepartingFareFamily?.feederEstimatedPrice,
      optionLegBuffer: selectedDepartingFareFamily?.feederBufferPrice,
    })
    const shownDepartingDelta = computeDepartingFeederDelta({
      optionFare: selectedDepartingFare,
      baseBudget: depRef.baseBudget,
      bufferBudget: depRef.bufferBudget,
    })
    return getModalLabelsFromDeltas(
      fareFamilies,
      optionFareFor,
      resolveReturningPageDelta(bundleBudget, selectedDepartingFare, shownDepartingDelta),
    )
  }
  return getModalLabelsFromDeltas(
    fareFamilies,
    optionFareFor,
    resolveDepartingPageDelta(bundleBudget, fareFamilies, optionFares),
  )
}

export default function getFareFamilyFeederBudgetLabels(options: Options): Map<string, number> | undefined {
  if (!options.bundleBudget) return undefined

  if (options.mode === FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_SINGLE) {
    return getSingleModalFeederBudgetLabels({
      ...options,
      bundleBudget: options.bundleBudget,
    })
  }

  return getCombinedModalFeederBudgetLabels({
    ...options,
    bundleBudget: options.bundleBudget,
  })
}
